import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription, combineLatest } from 'rxjs';
import { Broadcast } from 'src/app/models/Broadcast';
import { BroadcastContact } from 'src/app/models/BroadcastContact';
import { BroadcastsService } from 'src/app/shared/services/broadcasts.service';

import { IMPORTING, SCHEDULED, SENDING, BroadcastStatusTone, broadcastStatusTone } from '../../../models/BroadcastStatus';
import { PaginatedResult } from './../../../shared/model/paginated-result';
import { CopyService } from './../../../shared/services/copy.service';
import { deliveryStatusLabel } from '../../../shared/utils';
import { NUMBER } from 'src/app/models/BroadcastType';
import { AuthService } from 'src/app/shared/services/auth.service';
import { User } from 'src/app/models/User';
import { ADMINISTRATOR } from 'src/app/shared/services/user.service';
import { ProviderAccountService } from 'src/app/shared/services/provider-account.service';

@Component({
  selector: 'app-view-broadcast',
  templateUrl: './view-broadcast.component.html',
  styleUrls: ['./view-broadcast.component.scss']
})
export class ViewBroadcastComponent implements OnInit, OnDestroy {
  page = 1;
  perPage = 8;
  // "Verificar Contatos": os números vêm paginados do backend, uma página por vez.
  // Antes a lista era concatenada no scroll e o painel abria vazio.
  readonly numbersPerPage = 500;
  numbers: PaginatedResult<BroadcastContact[]>;
  numbersText = '';
  loadingNumbers = false;
  // Página que falhou ao carregar (null sem erro). A paginação já marcou essa página
  // no clique, então a textarea fica vazia: nada da página anterior pode ser copiado
  // como se fosse ela.
  numbersFailedPage: number = null;
  // Um request por vez: trocar de página ou fechar cancela o anterior, senão uma
  // resposta fora de ordem mostraria (e copiaria) outra página.
  private numbersRequest: Subscription;
  broadcastId: string;
  broadcast: Broadcast;
  contacts: PaginatedResult<BroadcastContact[]>;

  /** A tela inteira depende do broadcast: sem flag, ela fica em branco até chegar. */
  loading = true;
  loadingContacts = true;
  user: User;
  showNumbers = false;
  providerAccountsNames: object = {};
  phone: string = '';
  
  
  subscriptions: Subscription[] = [];

  get TypeNumber() {
    return this.broadcast.broadcast_type_id === NUMBER
  }
  
  get isScheduled() {
    return this.broadcast.broadcast_status_id === SCHEDULED;
  }

  get isImporting() {
    return this.broadcast.broadcast_status_id === IMPORTING;
  }

  get isSending() {
    return this.broadcast.broadcast_status_id === SENDING;
  }

  get isCancelled(): boolean {
    return this.broadcastsService.isCancelled(this.broadcast);
  }

  get waitingCount(): number {
    return this.broadcast.leads_count - this.broadcast.total_sent - this.broadcast.total_errors;
  }

  get statusTone(): BroadcastStatusTone {
    return broadcastStatusTone(this.broadcast.broadcast_status_id);
  }

  contactStatusTone(contact: BroadcastContact): BroadcastStatusTone {
    const status = this.getStatus(contact);
    return status === 'Enviado' ? 'ok' : status === 'Cancelado' ? 'danger' : 'warn';
  }

  get isAdministrator(): boolean {
    return this.user.profile_id === ADMINISTRATOR;
  }

  get laterThan90Days() {
    const now = new Date();
    const ninetyDaysAgo = new Date(now.setDate(now.getDate() - 90));
  
    const scheduledDate = 
      typeof this.broadcast.scheduled_date === 'string' 
        ? new Date(this.broadcast.scheduled_date) 
        : this.broadcast.scheduled_date;
  
    return scheduledDate <= ninetyDaysAgo;
  }
  

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private copyService: CopyService,
    private broadcastsService: BroadcastsService,
    private authService: AuthService,
    private providerAccountService: ProviderAccountService
  ) { }

  ngOnInit(): void {
    this.user = this.authService.getUser(true);

    // Combina params e queryParams para fazer apenas uma chamada
    this.subscriptions.push(
      combineLatest([
        this.route.params,
        this.route.queryParams
      ]).subscribe(([params, queryParams]) => {
        if (this.broadcastId && this.broadcastId !== params.id) {
          this.closeNumbers();
        }
        this.broadcastId = params.id;
        this.page = parseInt(queryParams.page) || 1;
        this.findById();
        this.listContacts();
      })
    );

    if (this.isAdministrator) {
      this.subscriptions.push(
        this.providerAccountService.listAll()
          .subscribe(providerAccounts => {
            this.providerAccountsNames = providerAccounts.reduce((acc, item) => {
              acc[item.id] = item.name;
              return acc;
            }, {});
          })
        );
      }
    }

  ngOnDestroy(): void {
    this.subscriptions.forEach(s => s.unsubscribe());
    this.numbersRequest?.unsubscribe();
  }

  toggleNumbers(): void {
    if (this.showNumbers) {
      this.closeNumbers();
      return;
    }

    this.showNumbers = true;
    this.loadNumbers(1);
  }

  closeNumbers(): void {
    this.numbersRequest?.unsubscribe();
    this.showNumbers = false;
    this.loadingNumbers = false;
    this.numbersFailedPage = null;
    this.numbers = null;
    this.numbersText = '';
  }

  loadNumbers(page: number): void {
    this.numbersRequest?.unsubscribe();
    this.loadingNumbers = true;
    this.numbersFailedPage = null;
    let received = false;
    this.numbersRequest = this.broadcastsService
      .listContacts(this.broadcastId, page, this.numbersPerPage)
      .subscribe({
        next: numbers => {
          received = true;
          this.numbers = numbers;
          this.numbersText = numbers.data.map(contact => contact.phone).filter(phone => phone).join('\n');
          this.loadingNumbers = false;
        },
        error: () => this.failNumbers(page),
        // Rede: na betaHml o HttpErrorInterceptor sempre relança, então a falha cai
        // no `error`. O `complete` sem `next` fica como guarda para resposta vazia.
        complete: () => {
          if (!received) {
            this.failNumbers(page);
          }
        }
      });
  }

  retryNumbers(): void {
    this.loadNumbers(this.numbersFailedPage);
  }

  private failNumbers(page: number): void {
    this.loadingNumbers = false;
    this.numbersFailedPage = page;
    this.numbersText = '';
  }

  copyNumbers(): void {
    this.copyService.copy('broadcast-numbers', 'Números desta página copiados!');
  }

  findById() {
    this.loading = true;
    this.subscriptions.push(
      this.broadcastsService.findById(this.broadcastId).subscribe(
        broadcast => {
          this.loading = false;
          this.broadcast = broadcast
        },
        () => {
          this.loading = false;
          this.router.navigate(['/broadcasts']);
        }
      )
    );
  }

  filterByPhone(): void{
    this.page = 1
    this.listContacts(this.phone)
  }

  listContacts(phone?: string): void {
    this.loadingContacts = true;
    this.subscriptions.push(
      this.broadcastsService
        .listContacts(this.broadcastId, this.page, this.perPage, phone)
        .subscribe({
          next: contacts => {
            this.loadingContacts = false;
            this.contacts = contacts;
          },
          error: () => { this.loadingContacts = false; },
        })
    );
  }

  getStatus(contact: BroadcastContact): string {
    if (contact.sent_date && !contact.cancelled) {
      return 'Enviado';
    }

    if (contact.cancelled) {
      return 'Cancelado';
    }

    return 'Pendente';
  }

  pageChanged(page: any): void {
    this.router
      .navigate(['/broadcasts/view', this.broadcast.id], { queryParams: { page } });
  }

  copyUrl(): void {
    this.copyService.copy('url', 'URL copiada com sucesso!');
  }

  copyShortUrl(): void {
    this.copyService.copy('short_url', 'URL Curta copiada com sucesso!');
  }

  /** Status de entrega do contato (DLR) — ver deliveryStatusLabel em utils. */
  getDeliveryStatus(contact: BroadcastContact): string {
    return deliveryStatusLabel(
      contact?.external_status,
      contact?.provider_status_description,
      contact?.webhook_status,
    );
  }

}
